"use client";

import { Spinner } from "@/components/ui/spinner";
import { useId, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FormOption {
  value: string;
  label: string;
  displayLabel?: string;
}

interface SearchableComboboxProps {
  name: string;
  label: string;
  placeholder?: string;
  searchPlaceholder?: string;
  triggerClassName?: string;
  required?: boolean;
  disabled?: boolean;
  displayValue?: boolean;
  hideLabel?: boolean;
  options: FormOption[];
  loading?: boolean;
  initialOption?: FormOption | null;
  onSearch?: (searchTerm: string) => void;
  onSelect?: (option: FormOption | null) => void;
  onOpenChange?: (open: boolean) => void;
  onTriggerHover?: () => void;
  error?: string | null;
  onRetry?: () => void;
  searchMode?: 'external' | 'local';
  localFilterFields?: Array<'label' | 'value' | 'displayLabel'>;
}

export function SearchableCombobox({
  name,
  label,
  placeholder = "Selecciona una opción",
  searchPlaceholder = "Buscar...",
  triggerClassName,
  required = false,
  disabled = false,
  displayValue = false,
  hideLabel = false,
  options,
  loading = false,
  initialOption = null,
  onSearch,
  onSelect,
  onOpenChange,
  onTriggerHover,
  error = null,
  onRetry,
  searchMode = 'external',
  localFilterFields = ['label', 'value', 'displayLabel'],
}: SearchableComboboxProps) {
  const { control, setValue } = useFormContext();
  const listId = useId();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const watchedValue = useWatch({ control, name });
  const watchedLabel = useWatch({ control, name: `${name}_label` });

  const watchedValueStr = watchedValue != null && watchedValue !== '' ? String(watchedValue) : '';
  const watchedLabelStr = watchedLabel != null ? String(watchedLabel).trim() : '';
  const syntheticInitialOption =
    watchedValueStr && (!initialOption || initialOption.value !== watchedValueStr)
      ? {
          value: watchedValueStr,
          label: watchedLabelStr || watchedValueStr,
          displayLabel: watchedLabelStr || undefined,
        }
      : null;
  const mergedOptions =
    syntheticInitialOption &&
    !options.some((option) => option.value === syntheticInitialOption.value)
      ? [syntheticInitialOption, ...options]
      : options;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearch('');
    }
    if (!disabled) {
      onOpenChange?.(newOpen);
    }
  };

  const handleSearchChange = (searchTerm: string) => {
    if (disabled) {
      return;
    }
    setSearch(searchTerm);
    onSearch?.(searchTerm);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const { onChange, value } = field;
        const normalizedValue = value != null && value !== '' ? String(value) : '';
        
        const normalizedSearch = search.trim().toLowerCase();
        const visibleOptions =
          searchMode === 'local' && normalizedSearch
            ? mergedOptions.filter((option) =>
                localFilterFields.some((fieldName) => {
                  const fieldValue = option[fieldName];
                  return String(fieldValue || '').toLowerCase().includes(normalizedSearch);
                })
              )
            : mergedOptions;

        const selectedOption =
          mergedOptions.find(option => option.value === normalizedValue) ||
          initialOption ||
          syntheticInitialOption;

        const handleSelect = (currentValue: string) => {
          if (disabled) {
            return;
          }

          const selected = mergedOptions.find(o => o.value === currentValue);
          const newValue = currentValue === normalizedValue ? "" : currentValue;
          
          setValue(name, newValue, { shouldValidate: false });
          
          if (newValue && selected) {
            setValue(`${name}_label`, selected.label);
          } else {
            setValue(`${name}_label`, "");
          }
          
          onSelect?.(newValue ? selected || null : null);
          onChange(newValue);
          setSearch('');
          onSearch?.('');
          setOpen(false);
        };

        return (
          <FormItem className="flex flex-col">
            <FormLabel className={cn(hideLabel && "sr-only")}>
              {label} {!hideLabel && required && '*'}
            </FormLabel>
            <Popover open={open} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={open ? listId : undefined}
                    disabled={disabled}
                    onMouseEnter={onTriggerHover}
                    className={cn(
                      "w-full min-w-0 justify-between",
                      !normalizedValue && "text-muted-foreground",
                      "disabled:bg-muted",
                      triggerClassName
                    )}
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-left"
                      title={
                        selectedOption
                          ? (displayValue ? selectedOption.value : selectedOption.label)
                          : placeholder
                      }
                    >
                      {selectedOption
                        ? (displayValue ? selectedOption.value : selectedOption.label)
                        : placeholder
                      }
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                onOpenAutoFocus={(event) => {
                  event.preventDefault();
                  searchInputRef.current?.focus();
                }}
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    ref={searchInputRef}
                    placeholder={searchPlaceholder}
                    value={search}
                    onValueChange={handleSearchChange}
                    aria-label={searchPlaceholder}
                  />
                  <CommandList id={listId}>
                    {error ? (
                      <div className="space-y-2 px-3 py-4 text-sm text-destructive" role="alert">
                        <p>No se pudieron cargar las opciones.</p>
                        {onRetry ? (
                          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                            Reintentar
                          </Button>
                        ) : null}
                      </div>
                    ) : loading && visibleOptions.length === 0 ? (
                      <div className="flex items-center justify-center py-6">
                        <Spinner />
                        <span className="ml-2 text-sm text-muted-foreground">Cargando...</span>
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>
                          {search.trim()
                            ? `No se encontraron resultados para "${search}"`
                            : 'No hay opciones disponibles'}
                        </CommandEmpty>
                        <CommandGroup>
                          {visibleOptions.map((option) => (
                            <CommandItem
                              key={option.value}
                              value={option.value}
                              onSelect={handleSelect}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  normalizedValue === option.value ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="min-w-0 flex-1 truncate" title={option.displayLabel || option.label}>
                                {option.displayLabel || option.label}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
            {error ? (
              <div className="flex items-center gap-2 text-sm text-destructive" role="alert">
                <span>{error}</span>
                {onRetry ? (
                  <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                    Reintentar
                  </Button>
                ) : null}
              </div>
            ) : null}
          </FormItem>
        );
      }}
    />
  );
}
